import React, { FunctionComponent, useContext } from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { PATHWAYS_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import { MobileAppPromoIllustration } from '../../../atoms/illustrations/MobileAppPromoIllustration'
import { MobileAppQRCode } from '../../../atoms/illustrations/MobileAppQRCode'
import { Title, Size as TitleSize } from '../../../atoms/title/Title'
import { AppleAppStoreBadge } from '../../../molecules/appleAppStoreBadge/AppleAppStoreBadge'
import { GooglePlayStoreBadge } from '../../../molecules/googlePlayStoreBadge/GooglePlayStoreBadge'
import { getAuroraAwareBackgroundColor } from '../../../styles/commonStyles'
import { ProductTypeContext } from '../../../utils/productType/productTypeContext'
import { ThemeType } from '../../../utils/themes/ThemeProvider'
import { tID } from '../../../utils/utils'

export interface MicrositeMobileAppPromoSectionProps {
  pathwaysType?: PATHWAYS_TYPE
  language?: string
  backgroundColor?: string
}

const Container = styled.View<{ theme: ThemeType; backgroundColor?: string }>(({ theme, backgroundColor }) => ({
  backgroundColor: getAuroraAwareBackgroundColor(
    theme.isAuroraTheme,
    () => backgroundColor || theme.colors.backgroundPrimary,
  ),
  flexDirection: theme.breakpoints.isMinWidthLaptop ? 'row' : 'column-reverse',
  padding: theme.breakpoints.isMinWidthLaptop
    ? '124px 120px'
    : theme.breakpoints.isMinWidthTablet
      ? '80px 64px'
      : '64px 24px',
  gap: theme.breakpoints.isMinWidthLaptop
    ? '80px'
    : theme.breakpoints.isMinWidthTablet
      ? theme.spacing['32px']
      : theme.spacing['24px'],
  alignItems: 'center',
  justifyContent: 'center',
}))

const ContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['16px'],
  alignItems: 'center',
  maxWidth: '672px',
  ...(theme.breakpoints.isMinWidthLaptop && {
    flex: 1,
  }),
}))

const BadgesContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
}))

export const MicrositeMobileAppPromoSection: FunctionComponent<MicrositeMobileAppPromoSectionProps> = ({
  pathwaysType,
  language,
  backgroundColor,
}) => {
  const {
    colors,
    isAuroraTheme,
    breakpoints: { isMinWidthTablet, isMinWidthLaptop, isMobileSized },
  } = useTheme() as ThemeType
  const { isLyraSelect } = useContext(ProductTypeContext)

  const isComingSoon = pathwaysType === PATHWAYS_TYPE.COMING_SOON
  const APP_STORE_BADGE_HEIGHT = 40
  const getAppPromoIconColor = () => {
    if (isAuroraTheme) {
      return colors.icons.lyraLogoAppPromoIcon?.fill
    }
    return isLyraSelect ? colors.iconActive : undefined
  }

  return (
    <Container
      testID={tID('MicrositeMobileAppPromoSection')}
      id='lyra-mobile-app-promo-section'
      backgroundColor={backgroundColor}
    >
      <ContentContainer>
        {!isComingSoon && isMinWidthLaptop && <MobileAppQRCode fillColor={getAppPromoIconColor()} />}
        <Title
          text={
            isComingSoon ? (
              <FormattedMessage
                defaultMessage='The Lyra app can help you improve your wellbeing anytime, anywhere'
                description='Text to describe the benefits of the Lyra mobile app'
              />
            ) : (
              <FormattedMessage
                defaultMessage='Download the Lyra app to improve your wellbeing anytime, anywhere'
                description='Text to encourage the user to download the Lyra mobile app'
              />
            )
          }
          size={isMobileSized ? TitleSize.SMALL : TitleSize.LARGE}
          level='2'
          textAlign='center'
        />
        {!isComingSoon && (
          <BadgesContainer testID={tID('MicrositeMobileAppPromoSection-AppStoreBadges')}>
            <AppleAppStoreBadge height={APP_STORE_BADGE_HEIGHT} locale={language} />
            <GooglePlayStoreBadge height={APP_STORE_BADGE_HEIGHT} locale={language} />
          </BadgesContainer>
        )}
      </ContentContainer>
      <MobileAppPromoIllustration
        width={isMinWidthLaptop ? 448 : isMinWidthTablet ? 356 : 263}
        height={isMinWidthLaptop ? 440 : isMinWidthTablet ? 350 : 278}
      />
    </Container>
  )
}
